import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Science as RunsIcon,
  CloudUpload as UploadIcon,
  AppRegistration as RegisterIcon,
} from '@mui/icons-material';
import { analysisFindingsAPI } from '../../services/api';

/**
 * Analysis Runs index page (#39).
 *
 * Table of `analysis_runs` ordered by run_number DESC, with header actions
 * to open the upload form or register a new pipeline. This is the natural
 * "list of analysis activity" surface, mirroring how Specimens / Projects /
 * Patients work — list view with action buttons, not a form-only page.
 *
 * Out of scope for v1 (separate follow-ups):
 *   - Per-run detail page (click row → /analysis/runs/:run_id)
 *   - Filters / sort / search
 *   - Bulk delete or other multi-row actions
 */

function formatUploader(run) {
  if (run.uploaded_by_first_name || run.uploaded_by_last_name) {
    return `${run.uploaded_by_first_name || ''} ${run.uploaded_by_last_name || ''}`.trim();
  }
  return run.uploaded_by_username || '—';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const STATUS_CHIP = {
  live: { label: 'Live', color: 'success' },
  superseded: { label: 'Superseded', color: 'default' },
  deleted: { label: 'Deleted', color: 'error' },
};

function StatusChip({ status }) {
  const s = STATUS_CHIP[status] || STATUS_CHIP.live;
  return <Chip label={s.label} color={s.color} size="small" variant="outlined" />;
}

const AnalysisRuns = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    analysisFindingsAPI
      .getRuns({
        include_superseded: includeSuperseded ? 'true' : undefined,
        include_deleted: includeDeleted ? 'true' : undefined,
      })
      .then((res) => {
        if (cancelled) return;
        setRuns(res.data.runs || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.msg || err.message || 'Failed to load analysis runs');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [includeSuperseded, includeDeleted]);

  return (
    <Box sx={{ p: 3, maxWidth: 1300 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
        <RunsIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5">Analysis Runs</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          component={RouterLink}
          to="/analysis/pipelines/register"
          variant="outlined"
          size="small"
          startIcon={<RegisterIcon />}
          sx={{ mr: 1 }}
        >
          Register pipeline
        </Button>
        <Button
          component={RouterLink}
          to="/analysis/upload"
          variant="contained"
          color="primary"
          size="small"
          startIcon={<UploadIcon />}
        >
          Upload analysis
        </Button>
      </Stack>

      <FormGroup row sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeSuperseded}
              onChange={(e) => setIncludeSuperseded(e.target.checked)}
            />
          }
          label="Show superseded"
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
          }
          label="Show deleted"
        />
      </FormGroup>

      <Paper sx={{ p: 0 }} elevation={1}>
        {loading && (
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading runs…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">Failed to load analysis runs: {error}</Alert>
          </Box>
        )}

        {!loading && !error && runs.length === 0 && (
          <Box sx={{ p: 3 }}>
            <Alert
              severity="info"
              action={
                <Button
                  component={RouterLink}
                  to="/analysis/upload"
                  color="inherit"
                  size="small"
                  startIcon={<UploadIcon />}
                >
                  Upload analysis
                </Button>
              }
            >
              No analysis runs yet. Upload an analysis output to get started.
            </Alert>
          </Box>
        )}

        {!loading && !error && runs.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Run</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Pipeline</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Uploaded by</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Findings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((r) => (
                  <TableRow
                    key={r.run_id}
                    hover
                    onClick={() => navigate(`/analysis/runs/${r.run_number}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>#{r.run_number}</TableCell>
                    <TableCell><StatusChip status={r.status} /></TableCell>
                    <TableCell>
                      {r.producer_name}
                      {r.producer_version && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          @ {r.producer_version}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{r.module_name}</TableCell>
                    <TableCell>{formatUploader(r)}</TableCell>
                    <TableCell>{formatDate(r.imported_at)}</TableCell>
                    <TableCell align="right">{Number(r.finding_count).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AnalysisRuns;
